import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import type {
  ApiResponse,
  Application,
  AuthCredentials,
  AuthResponse,
  Candidate,
  CandidateFilters,
  Channel,
  DatabaseSettings,
  FichePosteDto,
  FileUploadResponse,
  Interview,
  Job,
  JobFilters,
  LLMSettings,
  Need,
  NotificationData,
  Questionnaire,
  QuestionnaireProgress,
  SystemStats,
  User
} from '../types/api'


/**
 * Configuration de base pour l'API
 */
class ApiClient {
  private readonly axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081/api/v1',
      timeout: 30000,
      headers: {
        'Accept': 'application/json'
      }
    })

    this.setupInterceptors()
  }

  /**
   * Configuration des intercepteurs Axios
   */
  private setupInterceptors(): void {
    // Intercepteur de requête pour ajouter le token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Intercepteur de réponse pour gérer les erreurs
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token')
          window.location.href = '/login'
        }

        if (error.response?.status && error.response.status >= 500) {
          console.error('Erreur serveur:', error.response.data)
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Configuration du token d'authentification
   */
  public setAuthToken(token: string | null): void {
    if (token) {
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.axiosInstance.defaults.headers.common['Authorization']
    }
  }

  /**
   * Méthodes HTTP de base
   */
  public async get<T>(url: string, params?: any): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, { params })
  }

  public async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data)
  }

  public async put<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data)
  }

  public async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url)
  }

  /**
   * Upload de fichiers
   */
  public async uploadFile<T>(url: string, file: File, additionalData?: Record<string, any>): Promise<AxiosResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    return this.axiosInstance.post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  /**
   * Getter public pour la baseURL
   */
  public getBaseUrl(): string {
    return this.axiosInstance.defaults.baseURL?.replace(/\/$/, '') || ''
  }
}

// Instance unique de l'API client
const apiClient = new ApiClient()

/**
 * Service d'authentification
 */
export class AuthService {
  /**
   * Connexion utilisateur (mockée)
   */
  static async login(credentials: AuthCredentials): Promise<ApiResponse<AuthResponse>> {
    const mockUsers: Record<string, { password: string; user: User }> = {
      'hr@recruitment.com': {
        password: 'hr123',
        user: {
          id: 1,
          name: 'Responsable RH',
          email: 'hr@recruitment.com',
          roles: ['hr'],
          avatar: null
        }
      },
      'admin@recruitment.com': {
        password: 'admin123',
        user: {
          id: 2,
          name: 'Administrateur',
          email: 'admin@recruitment.com',
          roles: ['admin', 'hr'],
          avatar: null
        }
      },
      'nabil@email.com': {
        password: 'nabil123',
        user: {
          id: 3,
          name: 'Nabil EL HASSANI',
          email: 'nabil@email.com',
          roles: ['candidate'],
          avatar: null
        }
      }
    }

    // Simulation d'un délai réseau
    await new Promise(resolve => setTimeout(resolve, 1000))

    const user = mockUsers[credentials.email]
    if (user && user.password === credentials.password) {
      const token = `mock_token_${Date.now()}`
      return {
        data: {
          success: true,
          user: user.user,
          token,
          message: 'Connexion réussie'
        }
      }
    }

    throw new Error('Email ou mot de passe incorrect')
  }

  /**
   * Déconnexion utilisateur
   */
  static async logout(): Promise<ApiResponse<{ success: boolean }>> {
    await new Promise(resolve => setTimeout(resolve, 500))
    return { data: { success: true } }
  }

  /**
   * Inscription utilisateur
   */
  static async register(userData: Partial<User>): Promise<ApiResponse<AuthResponse>> {
    return apiClient.post<AuthResponse>('/auth/register', userData)
  }

  /**
   * Récupération du profil utilisateur (mockée)
   */
  static async getProfile(): Promise<ApiResponse<{ success: boolean; user: User }>> {
    await new Promise(resolve => setTimeout(resolve, 500))

    // Récupération du token depuis localStorage
    const token = localStorage.getItem('auth_token')
    if (!token) {
      throw new Error('Token d\'authentification manquant')
    }

    // Mock de récupération d'utilisateur basé sur le token
    const mockUsers: Record<string, User> = {
      'hr@recruitment.com': {
        id: 1,
        name: 'Responsable RH',
        email: 'hr@recruitment.com',
        roles: ['hr'],
        avatar: null
      },
      'admin@recruitment.com': {
        id: 2,
        name: 'Administrateur',
        email: 'admin@recruitment.com',
        roles: ['admin', 'hr'],
        avatar: null
      },
      'marie.dupont@email.com': {
        id: 3,
        name: 'Marie Dupont',
        email: 'marie.dupont@email.com',
        roles: ['candidate'],
        avatar: null
      }
    }

    // Simulation: décoder le "token" pour trouver l'utilisateur
    // En réalité, cela se ferait côté serveur
    const user = mockUsers['hr@recruitment.com'] // Par défaut on retourne l'utilisateur RH

    return {
      data: {
        success: true,
        user
      }
    }
  }

  /**
   * Mise à jour du profil utilisateur (mockée)
   */
  static async updateProfile(profileData: Partial<User>): Promise<ApiResponse<{ success: boolean; user: User }>> {
    await new Promise(resolve => setTimeout(resolve, 800))

    return {
      data: {
        success: true,
        user: {
          id: 1,
          name: 'Responsable RH',
          email: 'hr@recruitment.com',
          roles: ['hr'],
          avatar: null,
          ...profileData
        }
      }
    }
  }

  /**
   * Changement de mot de passe (mockée)
   */
  static async changePassword(_passwordData: { currentPassword: string; newPassword: string }): Promise<ApiResponse<{ success: boolean }>> {
    await new Promise(resolve => setTimeout(resolve, 1000))

    return {
      data: {
        success: true
      }
    }
  }

  /**
   * Réinitialisation de mot de passe (mockée)
   */
  static async resetPassword(_email: string): Promise<ApiResponse<{ success: boolean }>> {
    await new Promise(resolve => setTimeout(resolve, 1000))

    return {
      data: {
        success: true
      }
    }
  }
}

/**
 * Types pour la gestion des questionnaires côté candidat
 */
export type QuestionnaireAnswer = string | number | string[] | number[] | null

export interface QuestionnaireAnswers {
  [questionId: number]: QuestionnaireAnswer
}

export interface QuestionnaireSubmissionResponse {
  success: boolean
  message: string
  score: number
  feedback: string
  nextSteps: string
}

export interface QuestionnaireProgress {
  answers: QuestionnaireAnswers
  currentStep: number
  completed: boolean
}

/**
 * Service pour l'espace candidat
 */
export class CandidateSpaceService {
  /**
   * Récupération des candidatures
   */
  static async getApplications(): Promise<ApiResponse<Application[]>> {
    return apiClient.get<Application[]>('/candidate/applications')
  }

  /**
   * Récupération d'une candidature spécifique
   */
  static async getApplication(id: number): Promise<ApiResponse<Application>> {
    return apiClient.get<Application>(`/candidate/applications/${id}`)
  }

  /**
   * Récupération des questionnaires disponibles pour le candidat selon la fiche de poste
   * @param fichePosteId ID de la fiche de poste (job)
   * @returns {Promise<ApiResponse<{ success: boolean; questionnaires: Questionnaire[] }>>}
   */
  static async getQuestionnaires(fichePosteId: string): Promise<AxiosResponse<any>> {
    return apiClient.get(`/candidate/questionnaires/${fichePosteId}`)
  }

  /**
   * Soumission d'un questionnaire par le candidat
   * @param questionnaireId ID du questionnaire
   * @param answers Réponses du candidat (clé: question, valeur: réponse)
   */
  static async submitQuestionnaire(questionnaireId: string, candidatureDto: any): Promise<ApiResponse<{ status: string }>> {
    console.log('Soumission du questionnaire du candidat:', candidatureDto)
    return apiClient.post<{ status: string }>(`/candidate/questionnaires/${questionnaireId}/submit`, candidatureDto)
  }

  /**
   * Sauvegarde de la progression d'un questionnaire (auto-save)
   * @param questionnaireId ID du questionnaire
   * @param progress Données de progression (questions répondues, etc.)
   * @returns {Promise<ApiResponse<{ success: boolean; message: string }>>}
   */
  static async saveQuestionnaireProgress(
    questionnaireId: number,
    progress: QuestionnaireProgress
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        data: {
          success: true,
          message: 'Progression sauvegardée'
        }
      }
    } catch (error) {
      return {
        data: {
          success: false,
          message: 'Erreur lors de la sauvegarde de la progression'
        },
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      } as any
    }
  }

  /**
   * Récupération des entretiens
   */
  static async getInterviews(): Promise<ApiResponse<Interview[]>> {
    return apiClient.get<Interview[]>('/candidate/interviews')
  }

  /**
   * Mise à jour du profil candidat
   */
  static async updateProfile(profileData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.put<User>('/candidate/profile', profileData)
  }
}

export { apiClient }

/**
 * Service pour les candidatures publiques
 */
export class CandidateService {
  /**
   * Vérification du statut d'une candidature
   */
  static async checkStatus(email: string, jobId: number): Promise<ApiResponse<{ status: string }>> {
    return apiClient.post<{ status: string }>('/candidate/status', { email, jobId })
  }

  /**
   * Soumission d'une candidature
   */
  static async submitApplication(applicationData: FormData | Partial<Application>): Promise<ApiResponse<Application>> {
    console.log('Soumission de candidature - données reçues:', applicationData)
    return apiClient.post('/candidate/apply', applicationData)
  }

  /**
   * Récupération des offres d'emploi
   */
  static async getJobs(filters?: JobFilters): Promise<ApiResponse<Job[]>> {
    return apiClient.get<Job[]>('/candidate/jobs', filters)
  }

  /**
   * Récupération des informations d'un poste par son ID
   */
  static async getJobById(jobId: string): Promise<ApiResponse<{ id: string, jobId: string, title: string, isValidate: boolean }>> {
    return apiClient.get<{ id: string, jobId: string, title: string, isValidate: boolean }>(`/candidate/job/${jobId}`)
  }

  /**
   * Upload de CV
   */
  static async uploadCV(file: File): Promise<ApiResponse<FileUploadResponse>> {
    return apiClient.uploadFile<FileUploadResponse>('/candidate/upload-cv', file)
  }

  /**
   * Upload de vidéo de présentation
   * @param candidatureId ID de la candidature
   * @param file Fichier vidéo à uploader
   */
  static async uploadVideo(candidatureId: string, file: File): Promise<ApiResponse<FileUploadResponse>> {
    return apiClient.uploadFile<FileUploadResponse>(`/candidate/${candidatureId}/upload-video`, file)
  }

}

/**
 * Service RH
 */
export class HRService {
  /**
   * Dashboard et analytics
   */
  static async getAnalytics(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/hr/analytics')
  }

  static async getDashboardStats(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/hr/dashboard')
  }

  /**
   * Gestion des besoins
   */
  static async getNeeds(): Promise<ApiResponse<Need[]>> {
    return apiClient.get<Need[]>('/hr/needs')
  }

  static async getNeed(id: number): Promise<ApiResponse<Need>> {
    return apiClient.get<Need>(`/hr/needs/${id}`)
  }

  static async createNeed(needData: Partial<Need>): Promise<ApiResponse<Need>> {
    return apiClient.post<Need>('/hr/needs', needData)
  }

  static async updateNeed(id: number, needData: Partial<Need>): Promise<ApiResponse<Need>> {
    return apiClient.put<Need>(`/hr/needs/${id}`, needData)
  }

  static async deleteNeed(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/hr/needs/${id}`)
  }

  static async validateNeed(id: number): Promise<ApiResponse<Need>> {
    return apiClient.post<Need>(`/hr/needs/${id}/validate`)
  }

  static async closeNeed(id: number): Promise<ApiResponse<Need>> {
    return apiClient.post<Need>(`/hr/needs/${id}/close`)
  }

  /**
   * Gestion des postes
   */
  static async getJobs(): Promise<ApiResponse<Job[]>> {
    return apiClient.get<Job[]>('/hr/jobs')
  }

  static async getJob(id: string): Promise<ApiResponse<FichePosteDto>> {
    return apiClient.get<FichePosteDto>(`/hr/jobs/${id}`)
  }

  static async createJob(jobData: Partial<Job>): Promise<ApiResponse<Job>> {
    return apiClient.post<Job>('/hr/jobs', jobData)
  }

  static async updateJob(id: string, jobData: Partial<FichePosteDto>): Promise<ApiResponse<FichePosteDto>> {
    return apiClient.put<FichePosteDto>(`/hr/jobs/${id}`, jobData)
  }

  static async deleteJob(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/hr/jobs/${id}`)
  }

  static async publishJob(id: number, publishData: any): Promise<ApiResponse<Job>> {
    return apiClient.post<Job>(`/hr/jobs/${id}/publish`, publishData)
  }

  static async validateJob(id: string | number): Promise<ApiResponse<Job>> {
    return apiClient.post<Job>(`/hr/jobs/${id}/validate`)
  }

  static async generateQuestionnaire(jobId: number): Promise<ApiResponse<Questionnaire>> {
    return apiClient.post<Questionnaire>(`/hr/jobs/${jobId}/generate-questionnaire`)
  }

  /**
   * Gestion des candidats
   */
  static async getCandidates(): Promise<ApiResponse<Candidate[]>> {
    return apiClient.get<Candidate[]>('/hr/candidates')
  }

  static async getCandidate(id: number): Promise<ApiResponse<Candidate>> {
    return apiClient.get<Candidate>(`/hr/candidates/${id}`)
  }

  /**
   * Récupère l'URL de la vidéo de présentation d'un candidat
   */
  static getCandidatePresentationUrl(id: number): string {
    return apiClient.getBaseUrl() + `/hr/candidates/${id}/presentation`
  }
  static async updateCandidateStatus(id: number, statusData: { status: string; notes?: string }): Promise<ApiResponse<Candidate>> {
    return apiClient.put<Candidate>(`/hr/candidates/${id}/status`, statusData)
  }

  static async scheduleInterview(candidateId: number, interviewData: Partial<Interview>): Promise<ApiResponse<Interview>> {
    return apiClient.post<Interview>(`/hr/candidates/${candidateId}/interview`, interviewData)
  }

  static async addCandidateNote(id: number, note: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.post<{ success: boolean }>(`/hr/candidates/${id}/notes`, { note })
  }

  static async downloadCV(candidateId: number): Promise<AxiosResponse<Blob>> {
    return apiClient["axiosInstance"].get<Blob>(`/hr/candidates/${candidateId}/cv`, { responseType: 'blob' })
  }

  /**
   * Questionnaires et évaluations
   */
  static async getQuestionnaires(): Promise<ApiResponse<Questionnaire[]>> {
    return apiClient.get<Questionnaire[]>('/hr/questionnaires')
  }

  static async createQuestionnaire(questionnaireData: Partial<Questionnaire>): Promise<ApiResponse<Questionnaire>> {
    return apiClient.post<Questionnaire>('/hr/questionnaires', questionnaireData)
  }

  static async updateQuestionnaire(id: number, questionnaireData: Partial<Questionnaire>): Promise<ApiResponse<Questionnaire>> {
    return apiClient.put<Questionnaire>(`/hr/questionnaires/${id}`, questionnaireData)
  }

  static async deleteQuestionnaire(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/hr/questionnaires/${id}`)
  }

  /**
   * Rapports
   */
  static async exportCandidates(filters?: CandidateFilters): Promise<AxiosResponse<Blob>> {
    return apiClient.get<Blob>('/hr/reports/candidates', filters)
  }

  static async exportJobs(filters?: JobFilters): Promise<AxiosResponse<Blob>> {
    return apiClient.get<Blob>('/hr/reports/jobs', filters)
  }

  /**
   * Téléchargement du PDF d'un besoin
   */
  static async downloadNeedPdf(id: number): Promise<AxiosResponse<Blob>> {
    // @ts-ignore
    return apiClient["axiosInstance"].get(`/hr/needs/${id}/download`, { responseType: 'blob' })
  }

  /**
   * Upload d'une fiche de besoin
   */
  static async uploadNeed(file: File, additionalData: { manager: string; department: string }): Promise<ApiResponse<Need>> {
    return apiClient.uploadFile<Need>('/hr/needs/upload', file, additionalData)
  }
}

/**
 * Service d'administration
 */
export class AdminService {
  /**
   * Dashboard et système
   */
  static async getSystemStats(): Promise<ApiResponse<SystemStats>> {
    return apiClient.get<SystemStats>('/admin/system')
  }

  static async getSystemLogs(filters?: any): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>('/admin/logs', filters)
  }

  /**
   * Gestion des utilisateurs
   */
  static async getUsers(): Promise<ApiResponse<User[]>> {
    return apiClient.get<User[]>('/admin/users')
  }

  static async createUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.post<User>('/admin/users', userData)
  }

  static async updateUser(id: number, userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.put<User>(`/admin/users/${id}`, userData)
  }

  static async deleteUser(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/admin/users/${id}`)
  }

  static async resetUserPassword(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.post<{ success: boolean }>(`/admin/users/${id}/reset-password`)
  }

  /**
   * Configuration LLM
   */
  static async getLLMSettings(): Promise<ApiResponse<LLMSettings>> {
    return apiClient.get<LLMSettings>('/admin/llm/settings')
  }

  static async updateLLMSettings(settings: Partial<LLMSettings>): Promise<ApiResponse<LLMSettings>> {
    return apiClient.put<LLMSettings>('/admin/llm/settings', settings)
  }

  static async testLLMConnection(settings: Partial<LLMSettings>): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post<{ success: boolean; message: string }>('/admin/llm/test', settings)
  }

  static async getLLMPrompts(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/admin/llm/prompts')
  }

  static async updateLLMPrompts(prompts: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>('/admin/llm/prompts', prompts)
  }

  /**
   * Configuration base de données
   */
  static async getDatabaseSettings(): Promise<ApiResponse<DatabaseSettings>> {
    return apiClient.get<DatabaseSettings>('/admin/database/settings')
  }

  static async updateDatabaseSettings(settings: Partial<DatabaseSettings>): Promise<ApiResponse<DatabaseSettings>> {
    return apiClient.put<DatabaseSettings>('/admin/database/settings', settings)
  }

  static async testDatabaseConnection(settings: Partial<DatabaseSettings>): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post<{ success: boolean; message: string }>('/admin/database/test', settings)
  }

  static async backupDatabase(): Promise<AxiosResponse<Blob>> {
    return apiClient.post<Blob>('/admin/database/backup')
  }

  static async getDatabaseStats(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/admin/database/stats')
  }

  /**
   * Canaux de publication
   */
  static async getChannels(): Promise<ApiResponse<Channel[]>> {
    return apiClient.get<Channel[]>('/admin/channels')
  }

  static async createChannel(channelData: Partial<Channel>): Promise<ApiResponse<Channel>> {
    return apiClient.post<Channel>('/admin/channels', channelData)
  }

  static async updateChannel(id: number, channelData: Partial<Channel>): Promise<ApiResponse<Channel>> {
    return apiClient.put<Channel>(`/admin/channels/${id}`, channelData)
  }

  static async deleteChannel(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/admin/channels/${id}`)
  }

  static async testChannel(id: number): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post<{ success: boolean; message: string }>(`/admin/channels/${id}/test`)
  }

  /**
   * Configuration générale
   */
  static async getSettings(): Promise<ApiResponse<any>> {
    return apiClient.get<any>('/admin/settings')
  }

  static async updateSettings(settings: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>('/admin/settings', settings)
  }

  /**
   * Maintenance
   */
  static async clearCache(): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.post<{ success: boolean }>('/admin/maintenance/clear-cache')
  }

  static async optimizeDatabase(): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.post<{ success: boolean }>('/admin/maintenance/optimize-db')
  }

  static async generateReport(reportType: string, filters?: any): Promise<AxiosResponse<Blob>> {
    return apiClient.post<Blob>('/admin/reports/generate', { type: reportType, filters })
  }
}

/**
 * Service d'upload de fichiers
 */
export class UploadService {
  /**
   * Upload de fichier générique
   */
  static async uploadFile(file: File, type: string = 'document'): Promise<ApiResponse<FileUploadResponse>> {
    return apiClient.uploadFile<FileUploadResponse>('/upload', file, { type })
  }

  /**
   * Upload d'avatar
   */
  static async uploadAvatar(file: File): Promise<ApiResponse<FileUploadResponse>> {
    return apiClient.uploadFile<FileUploadResponse>('/upload/avatar', file)
  }
}

/**
 * Service de notifications
 */
export class NotificationService {
  /**
   * Récupération des notifications
   */
  static async getNotifications(): Promise<ApiResponse<NotificationData[]>> {
    return apiClient.get<NotificationData[]>('/notifications')
  }

  /**
   * Marquer une notification comme lue
   */
  static async markAsRead(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.put<{ success: boolean }>(`/notifications/${id}/read`)
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  static async markAllAsRead(): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.put<{ success: boolean }>('/notifications/read-all')
  }

  /**
   * Supprimer une notification
   */
  static async deleteNotification(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(`/notifications/${id}`)
  }
}

/**
 * Gestionnaire d'erreurs API
 */
export class ApiErrorHandler {
  /**
   * Gestion centralisée des erreurs d'API
   */
  static handle(error: AxiosError): string {
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          return (data as any)?.message ?? 'Données invalides'
        case 401:
          return 'Non autorisé - Veuillez vous reconnecter'
        case 403:
          return 'Accès interdit'
        case 404:
          return 'Ressource non trouvée'
        case 422:
          return (data as any)?.message ?? 'Données de validation incorrectes'
        case 429:
          return 'Trop de requêtes - Veuillez patienter'
        case 500:
          return 'Erreur interne du serveur'
        default:
          return (data as any)?.message ?? `Erreur HTTP ${status}`
      }
    } else if (error.request) {
      return 'Erreur de connexion - Vérifiez votre connexion internet'
    } else {
      return error.message ?? 'Une erreur inattendue s\'est produite'
    }
  }
}

/**
 * API Service principal - point d'entrée unifié
 */
export const apiService = {
  auth: AuthService,
  candidate: CandidateService,
  candidateSpace: CandidateSpaceService,
  hr: HRService,
  admin: AdminService,
  upload: UploadService,
  notifications: NotificationService,
  client: apiClient,
  errorHandler: ApiErrorHandler
} as const

export default apiService
